import React from 'react'
import PropTypes from 'prop-types'
import { Button, Input, Container, Selector } from 'components/UIElements'
import Loader from 'components/D3Loader'
import DefaultProfile from '../../../../../assets/default-profile.png'
import ChangePasswordForm from './ChangePasswordForm'
import { goBack } from 'navigation'

import '../stylesheets/user.scss'

const HEADER_MAP = {
  create: 'Create User',
  edit: 'Edit User',
  profile: 'My Profile'
}

const ACCESS_OPTIONS = [
  { key: 'enroller', text: 'Enroller', value: 'enroller' },
  { key: 'researcher', text: 'Researcher', value: 'researcher' },
  { key: 'studyManager', text: 'Study Manager', value: 'studyManager' },
  { key: 'administrator', text: 'Administrator', value: 'administrator' }
]

export const ACCESS_INFO_MAP = {
  enroller: {
    header: 'Participants Only',
    content: 'Read and Write access to participants',
    text: ACCESS_OPTIONS[0].text
  },
  researcher: {
    header: 'Data Only',
    content: 'Read and Write access to Instruments and Dashboard',
    text: ACCESS_OPTIONS[1].text
  },
  studyManager: {
    header: 'Participants and Data',
    content:
      'Read and Write access to Participants, Instruments, and Dashboard',
    text: ACCESS_OPTIONS[2].text
  },
  administrator: {
    header: 'All Access',
    content:
      'Read and Write access to Participants, Users, Instruments, and Dashboard',
    text: ACCESS_OPTIONS[3].text
  }
}

class CreateUserPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = { fileName: null }
  }

  componentDidMount() {
    this.props.setNavBarProps({
      back: this.props.pageType === 'profile' ? undefined : '/users',
      close: this.props.pageType === 'profile' ? undefined : '/users',
      backText: this.props.pageType === 'profile' ? 'Back' : 'Users',
      closeText: 'Close'
    })
  }

  componentWillUnmount() {
    this.props.resetUserPage()
    this.props.setNavBarProps(null)
  }

  handleSave = e => {
    e.preventDefault()
    const pageType = this.props.pageType
    if (pageType === 'profile') {
      this.props.saveSelfToDatabase(
        this.props.user,
        this.props.newPasswordActive,
        this.props.changePassword,
        this.props.profilePic
      )
    } else {
      this.props.saveToDatabase(
        this.props.user,
        pageType,
        this.props.changePassword
      )
    }
  }

  render() {
    const {
      user,
      pageType,
      updateField,
      currentUser,
      newPasswordActive,
      showChangePassword,
      uploadProfilePic,
      deleteUser,
      profilePic
    } = this.props

    const isRoot = user.id === user.created_by
    const allowEditPermissions =
      !isRoot && pageType !== 'profile' && currentUser.scope.includes('wu')

    if (this.props.loading) return <Loader />

    return (
      <div className='create-user page'>
        <h2>{HEADER_MAP[pageType]}</h2>
        <Container row>
          <div className='column left'>
            <Input
              id='user-email'
              disabled={pageType !== 'create'}
              onChange={value => updateField('username', value.trim())}
              label='Email'
              value={user.username}
            />
            <Input
              id='user-nickname'
              disabled={pageType !== 'profile' && !allowEditPermissions}
              onChange={value => updateField('nickname', value)}
              label='Nickname'
              value={user.nickname}
            />
            <p className='label'>Access Level</p>
            <Selector
              infoMap={ACCESS_INFO_MAP}
              disabled={!allowEditPermissions}
              onSelect={item => updateField('role', item.value)}
              options={ACCESS_OPTIONS}
              selected={user.role === 'edit' ? 'administrator' : user.role}
            />
            {pageType === 'profile' && (
              <a onClick={showChangePassword}>Change Password</a>
            )}
            {newPasswordActive && <ChangePasswordForm {...this.props} />}
          </div>
          <div className='column right'>
            <img src={profilePic || DefaultProfile} />
            {pageType === 'profile' && (
              <a onClick={() => this.refs.fileUpload.click()}>
                <i className='fa fa-camera' />
                {this.state.fileName || 'Upload Image'}
              </a>
            )}
            <input
              className='file-input'
              type='file'
              id='user-profile-pic'
              ref='fileUpload'
              accept='.jpg,.jpeg,.png,.svg'
              onChange={() => {
                this.setState({ fileName: this.refs.fileUpload.files[0].name })
                uploadProfilePic(this.refs.fileUpload.files[0])
              }}
            />
          </div>
        </Container>
        {(pageType === 'profile' || allowEditPermissions) && (
          <div className='button-list flex-end'>
            {pageType === 'edit' && (
              <Button
                className='delete-button'
                content='Delete User'
                onClick={() => deleteUser(user.id)}
              />
            )}
            <Button onClick={goBack} content='Cancel' />
            <Button
              content={pageType === 'create' ? 'Submit' : 'Save Changes'}
              onClick={this.handleSave}
            />
          </div>
        )}
      </div>
    )
  }
}

CreateUserPage.propTypes = {
  loading: PropTypes.bool,
  user: PropTypes.object,
  pageType: PropTypes.string,
  deleteUser: PropTypes.func,
  updateField: PropTypes.func,
  currentUser: PropTypes.object,
  saveToDatabase: PropTypes.func,
  saveSelfToDatabase: PropTypes.func,
  setNavBarProps: PropTypes.func,
  changePassword: PropTypes.object,
  showChangePassword: PropTypes.func,
  updateNewPassword: PropTypes.func,
  newPasswordActive: PropTypes.bool,
  uploadProfilePic: PropTypes.func,
  resetUserPage: PropTypes.func,
  profilePic: PropTypes.string
}

export default CreateUserPage
